import { useEffect, useState } from "react";
import { endpoints, fmtTime, downloadRunPdf } from "@/lib/api";
import { VerifyForm } from "@/pages/Verify";
import { toast } from "sonner";
import { FileDown, Fingerprint } from "lucide-react";

export default function TraceAudit() {
  const [seals, setSeals] = useState([]);
  const [runs, setRuns] = useState([]);
  const [busy, setBusy] = useState(null);

  const load = () => {
    endpoints.seals().then(setSeals);
    endpoints.runs().then(setRuns);
  };
  useEffect(load, []);

  const exportRun = async (id) => {
    setBusy(id);
    try {
      const seal = await downloadRunPdf(id);
      toast.success("Signed PDF exported", { description: `seal ${seal}` });
      load();
    } catch (e) {
      toast.error("Export failed");
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="max-w-[1300px] px-12 py-14" data-testid="page-audit">
      <div className="tx-overline mb-2">Procurement & legal</div>
      <h1 className="font-heading font-black text-4xl mb-2">Trace Audit</h1>
      <p className="text-zinc-600 text-[14px] max-w-2xl mb-8">
        Every export produces a SHA-256 digest of the full run trace, sealed with a server-side
        HMAC-SHA256 signature. Anyone holding the PDF can verify it at <span className="font-mono text-zinc-700">/verify</span>.
      </p>

      <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_420px] gap-10">
        <div>
          <div className="tx-overline mb-3">Runs · export</div>
          <div className="border border-zinc-200 divide-y divide-zinc-200 mb-10" data-testid="audit-runs">
            {runs.slice(0, 12).map((r) => (
              <div key={r.id} data-testid="audit-run-row" className="px-5 py-3 flex items-center justify-between gap-4">
                <div className="min-w-0">
                  <div className="font-mono text-[11px] text-zinc-500">{r.id} · {r.status}</div>
                  <div className="text-[12px] text-zinc-700 line-clamp-1">{r.intent}</div>
                </div>
                <button
                  data-testid="audit-export-button"
                  disabled={busy === r.id}
                  onClick={() => exportRun(r.id)}
                  className="shrink-0 h-8 px-3 border border-zinc-300 hover:border-orange-500/60 hover:text-orange-600 text-[11px] font-mono flex items-center gap-2 transition-colors disabled:opacity-40"
                >
                  <FileDown className="w-3.5 h-3.5" /> {busy === r.id ? "sealing…" : "Signed PDF"}
                </button>
              </div>
            ))}
            {runs.length === 0 && <div className="px-5 py-6 text-[12px] text-zinc-500">No runs in this workspace yet.</div>}
          </div>

          <div className="tx-overline mb-3">Seals issued · {seals.length}</div>
          <div className="border border-zinc-200 overflow-x-auto" data-testid="seals-table">
            <table className="w-full text-[12px]">
              <thead className="bg-zinc-100 text-left">
                <tr className="tx-overline">
                  <th className="px-4 py-2.5">Seal</th>
                  <th className="px-4 py-2.5">Run</th>
                  <th className="px-4 py-2.5">Issued</th>
                  <th className="px-4 py-2.5">By</th>
                  <th className="px-4 py-2.5">Events</th>
                  <th className="px-4 py-2.5">Digest</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-200 font-mono">
                {seals.map((s) => (
                  <tr key={s.id} data-testid="seal-row" className="hover:bg-zinc-100">
                    <td className="px-4 py-2.5 text-orange-600">{s.id}</td>
                    <td className="px-4 py-2.5 text-zinc-700">{s.run_id}</td>
                    <td className="px-4 py-2.5 text-zinc-600">{fmtTime(s.issued_at)}</td>
                    <td className="px-4 py-2.5 text-zinc-600">{s.issued_by}</td>
                    <td className="px-4 py-2.5 text-zinc-600">{s.event_count}</td>
                    <td className="px-4 py-2.5 text-zinc-500">{s.digest.slice(0, 16)}…</td>
                  </tr>
                ))}
                {seals.length === 0 && (
                  <tr><td colSpan={6} className="px-4 py-6 text-zinc-500 font-sans">No seals issued yet.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div>
          <div className="flex items-center gap-2 mb-3">
            <Fingerprint className="w-4 h-4 text-orange-600" />
            <span className="tx-overline">Verify a seal</span>
          </div>
          <div className="border border-zinc-200 p-5 bg-zinc-50">
            <VerifyForm />
          </div>
        </div>
      </div>
    </div>
  );
}
